import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import { useAuth } from "../lib/auth";
import { endpoints } from "../lib/api";
import { Bookmark, Trash2, Plus, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, loading: authLoading, requestLogin } = useAuth();
  const [saved, setSaved] = useState([]);
  const [watchlists, setWatchlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, w] = await Promise.all([endpoints.savedList(), endpoints.watchlists()]);
      setSaved(s.data.items || []);
      setWatchlists(w.data.items || []);
    } catch (e) {
      // 401 — auth context handles
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  if (authLoading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="mx-auto max-w-md rounded-2xl border border-zinc-200 bg-white p-10 text-center soft-shadow" data-testid="dashboard-login-cta">
          <h1 className="font-display text-2xl font-bold text-zinc-900">Sign in to access your dashboard</h1>
          <p className="mt-2 text-sm text-zinc-600">Save domains, build watchlists, and get price alerts.</p>
          <button
            onClick={() => requestLogin("/dashboard")}
            data-testid="login-cta-btn"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-medium text-white hover:bg-indigo-500"
          >
            Continue with Google
          </button>
        </div>
      </Layout>
    );
  }

  const removeSaved = async (id) => {
    await endpoints.savedDelete(id);
    setSaved((s) => s.filter((x) => x.id !== id));
  };

  const createWatch = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    const res = await endpoints.watchlistCreate({ name: newName, domains: [] });
    setWatchlists((w) => [...w, res.data]);
    setNewName("");
    toast.success("Watchlist created");
  };

  const removeWatch = async (id) => {
    await endpoints.watchlistDelete(id);
    setWatchlists((w) => w.filter((x) => x.id !== id));
  };

  return (
    <Layout>
      <Seo title="Your dashboard" description="Saved domains and watchlists." path="/dashboard" noindex />
      <section data-testid="dashboard">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Dashboard</div>
            <h1 className="mt-2 font-display text-3xl font-bold text-zinc-900 md:text-4xl">Hi {user.name?.split(" ")[0] || "there"} 👋</h1>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Saved domains */}
          <div className="rounded-2xl border border-zinc-100 bg-white p-6 soft-shadow" data-testid="saved-domains-card">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-zinc-900">Saved domains</h2>
              <span className="text-xs text-zinc-500">{saved.length} items</span>
            </div>
            {loading ? (
              <Loader2 className="mt-6 h-4 w-4 animate-spin text-zinc-400" />
            ) : saved.length === 0 ? (
              <p className="mt-6 text-sm text-zinc-500">
                No saved domains yet. <Link to="/compare" className="text-indigo-600">Search a domain</Link> and click <Bookmark className="inline h-3 w-3" />.
              </p>
            ) : (
              <ul className="mt-4 divide-y divide-zinc-100">
                {saved.map((s) => (
                  <li key={s.id} className="flex items-center justify-between py-3" data-testid={`saved-${s.domain}`}>
                    <div>
                      <Link to={`/compare?name=${encodeURIComponent(s.domain)}`} className="font-mono text-sm font-medium text-zinc-900 hover:text-indigo-700">
                        {s.domain}
                      </Link>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-400">{s.source}</div>
                    </div>
                    <button onClick={() => removeSaved(s.id)} className="rounded-md p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600" data-testid={`del-saved-${s.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Watchlists */}
          <div className="rounded-2xl border border-zinc-100 bg-white p-6 soft-shadow" data-testid="watchlists-card">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-zinc-900">Watchlists</h2>
              <span className="text-xs text-zinc-500">{watchlists.length} lists</span>
            </div>
            <form onSubmit={createWatch} className="mt-4 flex gap-2" data-testid="watchlist-form">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New watchlist name…"
                className="flex-1 rounded-lg border border-zinc-200 bg-zinc-50/40 px-3 py-2 text-sm outline-none focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                data-testid="watchlist-name-input"
              />
              <button type="submit" className="inline-flex items-center gap-1 rounded-lg bg-zinc-900 px-3 py-2 text-xs font-medium text-white hover:bg-zinc-800" data-testid="watchlist-add-btn">
                <Plus className="h-3 w-3" /> Add
              </button>
            </form>
            {watchlists.length === 0 ? (
              <p className="mt-6 text-sm text-zinc-500">No watchlists yet.</p>
            ) : (
              <ul className="mt-4 divide-y divide-zinc-100">
                {watchlists.map((w) => (
                  <li key={w.id} className="flex items-center justify-between py-3" data-testid={`watchlist-${w.name}`}>
                    <div>
                      <div className="text-sm font-medium text-zinc-900">{w.name}</div>
                      <div className="text-xs text-zinc-500">{w.domains.length} domains</div>
                    </div>
                    <button onClick={() => removeWatch(w.id)} className="rounded-md p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-6" data-testid="upgrade-banner">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-display text-base font-semibold text-indigo-900">Upgrade to Pro for unlimited AI searches</div>
              <p className="mt-1 text-sm text-indigo-800/80">Free plan gets 5 AI Name Finder searches per month. Upgrade to Pro for unlimited generations and price-drop alerts on saved domains.</p>
            </div>
            <Link to="/pricing" data-testid="dashboard-see-plans" className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-500">
              See plans <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
